import { useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  GoodsReceiptDetailSchema,
  PurchaseOrderDetailSchema,
  PurchaseOrderSchema,
  type PurchaseOrderDto,
} from '@taqon/contracts';
import { getData } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { RecordLink } from '../finance/detail';
import { SearchPicker } from '../finance/lookups';

const ORDERS = '/api/v1/procurement/purchase-orders';
const RECEIPTS = '/api/v1/procurement/goods-receipts';

/** The procurement source an asset names (FRD-AST-012, AST-016): order line and GRN line ids. */
export interface ProcurementSource {
  readonly purchaseOrderLineId: string | null;
  readonly goodsReceiptLineId: string | null;
}

function usePurchaseOrder(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['procurement', 'purchase-order', id],
    enabled: enabled && id !== null,
    retry: false,
    queryFn: () => getData(`${ORDERS}/${id ?? ''}`, PurchaseOrderDetailSchema),
  });
}

/**
 * Chooses the purchase order line and, optionally, the goods receipt line an asset came from
 * (FRD-AST-012, AST-016). The order is searched on the server by number; its lines and receipts are
 * read through the purchasing API, so only orders the session may view are offered. When the line
 * is a candidate capital asset line, its asset class is handed to the form (FRD-AST-017), and the
 * API refuses any other class.
 */
export function ProcurementSourceFields({
  initialOrderId,
  value,
  onChange,
  onAssetClass,
}: {
  /** The order the stored source belongs to, when editing an asset that has one. */
  initialOrderId: string | null;
  value: ProcurementSource;
  onChange: (value: ProcurementSource) => void;
  onAssetClass: (assetClassId: string) => void;
}) {
  const session = useSession();
  const canSeeOrders = hasCapability(session.data, 'procurement.purchase_order:view');
  const canSeeReceipts = hasCapability(session.data, 'procurement.goods_receipt:view');
  const [order, setOrder] = useState<PurchaseOrderDto | null | undefined>(undefined);
  const orderId = order === undefined ? initialOrderId : (order?.id ?? null);
  const detail = usePurchaseOrder(orderId, canSeeOrders);
  const shownOrder = order === undefined ? (detail.data?.order ?? null) : order;
  const receipts = useQueries({
    queries: (canSeeReceipts ? (detail.data?.receipts ?? []) : []).map((receipt) => ({
      queryKey: ['procurement', 'goods-receipt', receipt.id],
      queryFn: () => getData(`${RECEIPTS}/${receipt.id}`, GoodsReceiptDetailSchema),
    })),
  });
  const receiptLines = receipts.flatMap((query) =>
    (query.data?.lines ?? [])
      .filter((line) => line.purchaseOrderLineId === value.purchaseOrderLineId)
      .map((line) => ({
        id: line.id,
        label: `${query.data?.receipt.documentNo ?? ''} line ${String(line.lineNo)} — ${line.quantityReceived} received`,
      })),
  );

  if (!canSeeOrders) {
    return (
      <Typography variant="caption" color="text.secondary">
        {value.purchaseOrderLineId === null
          ? 'Naming the purchase order the asset came from needs permission to view purchase orders.'
          : 'The asset names a purchase order you cannot view; it is kept as it is.'}
      </Typography>
    );
  }
  return (
    <Stack spacing={2}>
      <SearchPicker
        label="Purchase order"
        queryKey="purchase-orders"
        schema={PurchaseOrderSchema}
        value={shownOrder}
        path={(term) =>
          `${ORDERS}?pageSize=25&sort=-document_no` +
          (term === '' ? '' : `&filter[search]=${encodeURIComponent(term)}`)
        }
        describe={(o) => `${o.documentNo} — ${o.supplierLabel ?? ''}`}
        helperText="Optional: the order the asset was bought on"
        onChange={(next) => {
          setOrder(next);
          onChange({ purchaseOrderLineId: null, goodsReceiptLineId: null });
        }}
      />
      {orderId !== null && (
        <TextField
          select
          label="Order line"
          value={value.purchaseOrderLineId ?? ''}
          helperText={
            detail.isError
              ? 'The purchase order could not be read.'
              : 'A candidate capital asset line gives the asset its class (FRD-AST-017).'
          }
          onChange={(e) => {
            const lineId = e.target.value === '' ? null : e.target.value;
            onChange({ purchaseOrderLineId: lineId, goodsReceiptLineId: null });
            const line = detail.data?.lines.find((l) => l.id === lineId);
            if (line?.assetClassId != null) onAssetClass(line.assetClassId);
          }}
        >
          <MenuItem value="">None</MenuItem>
          {(detail.data?.lines ?? []).map((line) => (
            <MenuItem key={line.id} value={line.id}>
              Line {line.lineNo} — {line.description}
              {line.assetClassId === null ? '' : ' (asset line)'}
            </MenuItem>
          ))}
        </TextField>
      )}
      {value.purchaseOrderLineId !== null && canSeeReceipts && (
        <TextField
          select
          label="Goods receipt line"
          value={value.goodsReceiptLineId ?? ''}
          helperText={
            receiptLines.length === 0 ? 'Nothing has been received on this line yet.' : 'Optional'
          }
          onChange={(e) => {
            onChange({
              ...value,
              goodsReceiptLineId: e.target.value === '' ? null : e.target.value,
            });
          }}
        >
          <MenuItem value="">None</MenuItem>
          {receiptLines.map((line) => (
            <MenuItem key={line.id} value={line.id}>
              {line.label}
            </MenuItem>
          ))}
          {value.goodsReceiptLineId !== null &&
            !receiptLines.some((line) => line.id === value.goodsReceiptLineId) && (
              <MenuItem value={value.goodsReceiptLineId}>The recorded receipt line</MenuItem>
            )}
        </TextField>
      )}
    </Stack>
  );
}

/**
 * The procurement source on an asset's page (FRD-AST-012): the order and receipt, each linked and
 * named only when the session may read it.
 */
export function ProcurementSourceText({
  purchaseOrderId,
  purchaseOrderLineId,
  goodsReceiptId,
  goodsReceiptLineId,
}: {
  purchaseOrderId: string | null;
  purchaseOrderLineId: string | null;
  goodsReceiptId: string | null;
  goodsReceiptLineId: string | null;
}) {
  const session = useSession();
  const canSeeOrders = hasCapability(session.data, 'procurement.purchase_order:view');
  const canSeeReceipts = hasCapability(session.data, 'procurement.goods_receipt:view');
  const order = usePurchaseOrder(purchaseOrderId, canSeeOrders);
  const receipt = useQuery({
    queryKey: ['procurement', 'goods-receipt', goodsReceiptId],
    enabled: canSeeReceipts && goodsReceiptId !== null,
    retry: false,
    queryFn: () => getData(`${RECEIPTS}/${goodsReceiptId ?? ''}`, GoodsReceiptDetailSchema),
  });
  if (purchaseOrderLineId === null && goodsReceiptLineId === null) return <>—</>;
  const line = order.data?.lines.find((l) => l.id === purchaseOrderLineId);
  const receiptLine = receipt.data?.lines.find((l) => l.id === goodsReceiptLineId);
  return (
    <Stack spacing={0.5}>
      {purchaseOrderId !== null &&
        (order.data === undefined ? (
          <span>
            {canSeeOrders && order.isPending ? '…' : 'Purchase order (not visible to you)'}
          </span>
        ) : (
          <span>
            <RecordLink to={`/procurement/purchase-orders/${purchaseOrderId}`}>
              {order.data.order.documentNo}
            </RecordLink>
            {line === undefined ? '' : ` line ${String(line.lineNo)} — ${line.description}`}
          </span>
        ))}
      {goodsReceiptId !== null &&
        (receipt.data === undefined ? (
          <span>
            {canSeeReceipts && receipt.isPending ? '…' : 'Goods receipt (not visible to you)'}
          </span>
        ) : (
          <span>
            <RecordLink to={`/procurement/receipts/${goodsReceiptId}`}>
              {receipt.data.receipt.documentNo}
            </RecordLink>
            {receiptLine === undefined ? '' : ` line ${String(receiptLine.lineNo)}`}
          </span>
        ))}
    </Stack>
  );
}
